/**
 * RBAC Context (Phase 2.1 + 2.2) — Permission-aware Admin UI.
 *
 * PHASE 2.1: Fetches GET /api/rbac/me on mount and exposes helpers.
 *
 * PHASE 2.2: Optional "role preview" mode for Super Admins ONLY.
 *   - Preview state is stored in sessionStorage (cleared on browser
 *     close / refresh in a new tab / manual exit).
 *   - When previewing, `hasPerm / hasAny / isSuperAdmin` all reflect
 *     the PREVIEWED role, not the real Super Admin session.
 *   - `isPreviewing` and `previewRoleName` are exposed for the banner
 *     + mutation guards.
 *   - The real backend session is unchanged — this is UX-only.
 *   - Mutation blocking is enforced in `lib/api.js` (request interceptor).
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "./api";
import { useAuth } from "./auth";

const RbacCtx = createContext(null);

const EMPTY_PERMS = new Set();
const PREVIEW_KEY = "gf_rbac_preview_v1";

function _loadPreview() {
  try {
    const raw = sessionStorage.getItem(PREVIEW_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p?.role_id || !Array.isArray(p?.permissions)) return null;
    return p;
  } catch {
    return null;
  }
}

function _savePreview(p) {
  try {
    if (!p) sessionStorage.removeItem(PREVIEW_KEY);
    else sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(p));
  } catch {
    // sessionStorage full / disabled — silently ignore, preview will
    // simply not persist across route changes.
  }
}

export function RbacProvider({ children }) {
  const { user, loading: authLoading, logout } = useAuth();
  const [state, setState] = useState({
    permissions: EMPTY_PERMS,
    roleIds: [],
    userType: null,
    disabled: false,
    loading: true,
    loaded: false,
  });

  // Preview state — hydrated from sessionStorage on mount.
  // Shape: { role_id, role_name, permissions: string[] }
  const [preview, setPreview] = useState(() => _loadPreview());
  const previewRef = useRef(preview);
  useEffect(() => { previewRef.current = preview; _savePreview(preview); }, [preview]);

  const refresh = useCallback(async () => {
    if (!user || user._optimistic) return;
    try {
      const { data } = await api.get("/rbac/me");
      if (data?.disabled === true) {
        try { await logout(); } catch (e) { /* noop */ }
        return;
      }
      setState({
        permissions: new Set(data?.permissions || []),
        roleIds: data?.role_ids || [],
        userType: data?.user_type || null,
        disabled: false,
        loading: false,
        loaded: true,
      });
    } catch (e) {
      setState((s) => ({ ...s, permissions: EMPTY_PERMS, loading: false, loaded: true }));
    }
  }, [user, logout]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState({
        permissions: EMPTY_PERMS,
        roleIds: [],
        userType: null,
        disabled: false,
        loading: false,
        loaded: true,
      });
      // Clear any stale preview from a previous logged-in session.
      setPreview(null);
      return;
    }
    refresh();
  }, [authLoading, user, refresh]);

  // The REAL Super Admin status — unaffected by preview mode. Used to
  // decide whether the current session is allowed to START a preview.
  const isRealSuperAdmin = useMemo(() => {
    if (user?.role === "admin" && state.roleIds.length === 0) return true;
    return state.roleIds.includes("role_super_admin");
  }, [user, state.roleIds]);

  // The EFFECTIVE permissions from the UI's point of view — swapped
  // out for the previewed role's perm set when preview is active.
  const effectivePerms = useMemo(
    () => (preview ? new Set(preview.permissions || []) : state.permissions),
    [preview, state.permissions],
  );

  // isSuperAdmin from the UI's point of view — false during preview
  // even if the underlying session is Super Admin.
  const isSuperAdmin = useMemo(
    () => (preview ? false : isRealSuperAdmin),
    [preview, isRealSuperAdmin],
  );

  const hasPerm = useCallback(
    (code) => {
      if (!code) return true;
      if (isSuperAdmin) return true;
      return effectivePerms.has(code);
    },
    [effectivePerms, isSuperAdmin],
  );

  const hasAny = useCallback(
    (...codes) => {
      if (!codes || codes.length === 0) return true;
      if (isSuperAdmin) return true;
      return codes.some((c) => effectivePerms.has(c));
    },
    [effectivePerms, isSuperAdmin],
  );

  const hasAll = useCallback(
    (...codes) => {
      if (!codes || codes.length === 0) return true;
      if (isSuperAdmin) return true;
      return codes.every((c) => effectivePerms.has(c));
    },
    [effectivePerms, isSuperAdmin],
  );

  // ---- Preview API -------------------------------------------------
  const startPreview = useCallback(async (roleId) => {
    if (!isRealSuperAdmin) {
      throw new Error("Only Super Admin can start role preview");
    }
    const { data } = await api.post("/rbac/preview/start", { role_id: roleId });
    setPreview({
      role_id: data.role_id,
      role_name: data.role_name,
      permissions: data.permissions || [],
    });
    return data;
  }, [isRealSuperAdmin]);

  const endPreview = useCallback(async () => {
    const current = previewRef.current;
    if (!current) return;
    try {
      await api.post("/rbac/preview/end", { role_id: current.role_id });
    } catch (e) {
      // Even if audit write fails, always tear down local state so the
      // UI never gets stuck in preview.
    }
    setPreview(null);
  }, []);

  // Global signal for the axios request interceptor.
  useEffect(() => {
    window.__gfRbacPreview = preview ? { role_id: preview.role_id, role_name: preview.role_name } : null;
  }, [preview]);

  // Phase 2.2 lifecycle guarantee — preview mode ends on browser refresh
  // as well as on tab close (sessionStorage handles the latter naturally;
  // beforeunload handles the former). Prevents a preview left over from
  // a previous session from silently affecting the operator's next
  // pageview.
  useEffect(() => {
    const clearOnUnload = () => {
      try { sessionStorage.removeItem(PREVIEW_KEY); } catch (e) { /* noop */ }
    };
    window.addEventListener("beforeunload", clearOnUnload);
    return () => window.removeEventListener("beforeunload", clearOnUnload);
  }, []);

  const value = useMemo(
    () => ({
      permissions: effectivePerms,
      roleIds: state.roleIds,
      userType: state.userType,
      loading: state.loading,
      loaded: state.loaded,
      isSuperAdmin,
      isRealSuperAdmin,
      isPreviewing: !!preview,
      previewRoleId: preview?.role_id || null,
      previewRoleName: preview?.role_name || null,
      hasPerm,
      hasAny,
      hasAll,
      refresh,
      startPreview,
      endPreview,
    }),
    [effectivePerms, state, isSuperAdmin, isRealSuperAdmin, preview, hasPerm, hasAny, hasAll, refresh, startPreview, endPreview],
  );

  return <RbacCtx.Provider value={value}>{children}</RbacCtx.Provider>;
}

export function useRbac() {
  const ctx = useContext(RbacCtx);
  if (!ctx) {
    return {
      permissions: EMPTY_PERMS,
      roleIds: [],
      userType: null,
      loading: false,
      loaded: true,
      isSuperAdmin: false,
      isRealSuperAdmin: false,
      isPreviewing: false,
      previewRoleId: null,
      previewRoleName: null,
      hasPerm: () => false,
      hasAny: () => false,
      hasAll: () => false,
      refresh: () => {},
      startPreview: async () => {},
      endPreview: async () => {},
    };
  }
  return ctx;
}

export function Can({ perm, anyOf, allOf, fallback = null, children }) {
  const { hasPerm, hasAny, hasAll, loaded } = useRbac();
  if (!loaded) return null;
  let ok = true;
  if (perm) ok = ok && hasPerm(perm);
  if (anyOf && anyOf.length) ok = ok && hasAny(...anyOf);
  if (allOf && allOf.length) ok = ok && hasAll(...allOf);
  return ok ? <>{children}</> : fallback;
}
